/**
 * How a history row looks, wherever history is drawn (RFC 0313 3g, 3h).
 *
 * A document's History column and a session's Changes show the same rows,
 * from the same projection, so they draw them the same way: the text taken
 * out and put in, field lines for records, and Undo, Undone or a struck Undo.
 * A service because two features need it and a feature never imports another.
 */

import {
  type CommentAuthor,
  changeStatus,
  type FieldChange,
  type HistoryChange,
} from '@mod/core/model'
import { useMemo, useState } from 'react'

/** How removed and added text is marked everywhere history is drawn. */
export const removedMark =
  'rounded-sm bg-red-500/10 px-0.5 text-red-400 line-through'
export const addedMark = 'rounded-sm bg-green-500/10 px-0.5 text-green-400'

export function dayLabel(seconds: number, now: Date): string {
  const d = new Date(seconds * 1000)
  const startOf = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const days = Math.round((startOf(now) - startOf(d)) / 86_400_000)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  return d.toLocaleDateString(undefined, {
    weekday: days < 7 ? 'long' : undefined,
    day: 'numeric',
    month: 'short',
  })
}

/** A field change in a line: `status: draft → done`, `items: +1, −1`. */
export function fieldLine(f: FieldChange): string {
  if (f.kind === 'list') {
    const parts = [
      ...(f.added.length ? [`+${f.added.length}`] : []),
      ...(f.removed.length ? [`−${f.removed.length}`] : []),
    ]
    const named = [...f.added, ...f.removed].slice(0, 2).join(', ')
    return `${f.path}: ${parts.join(', ')}${named ? ` (${named})` : ''}`
  }
  const show = (v: unknown) => {
    if (v === undefined) return '∅'
    const s = typeof v === 'string' ? v : JSON.stringify(v)
    return s.length > 40 ? 'changed' : s
  }
  if (typeof f.before === 'object' || typeof f.after === 'object') {
    return `${f.path}: changed`
  }
  return `${f.path}: ${show(f.before)} → ${show(f.after)}`
}

export function clip(s: string, n = 80): string {
  const one = s.replace(/\s+/g, ' ').trim()
  return one.length > n ? `${one.slice(0, n - 1)}…` : one
}

export function time(seconds: number): string {
  return new Date(seconds * 1000).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function Face({ author }: { author: CommentAuthor | null }) {
  if (author?.avatarUrl) {
    return (
      <img
        src={author.avatarUrl}
        alt=""
        className="size-4 shrink-0 rounded-full"
      />
    )
  }
  return (
    <span
      className="flex size-4 shrink-0 items-center justify-center rounded-full font-semibold text-[8px] text-background"
      style={{ background: author?.tint ?? '#5a5a5a' }}
    >
      {author?.initials ?? '?'}
    </span>
  )
}

/** The text a row took out and put in. */
export function TextDiff({ row }: { row: HistoryChange }) {
  if (!row.text) return null
  const { removed, added } = row.text
  return (
    <span className="min-w-0">
      {removed && (
        <span className={removedMark}>{clip(removed, added ? 36 : 80)}</span>
      )}
      {removed && added && ' '}
      {added && (
        <span className={addedMark}>{clip(added, removed ? 36 : 80)}</span>
      )}
    </span>
  )
}

/** What one row did, in a line. */
export function RowSummary({ row }: { row: HistoryChange }) {
  if (row.reverts) {
    return (
      <span className="min-w-0">
        <span className="text-muted-foreground">Undid a change: </span>
        <TextDiff row={row} />
      </span>
    )
  }
  if (row.restores) {
    return (
      <span className="text-muted-foreground">Restored an earlier version</span>
    )
  }
  if (row.text) return <TextDiff row={row} />
  if (row.fields) {
    return (
      <span className="min-w-0 text-foreground/80">
        {row.fields.slice(0, 3).map(fieldLine).join(' · ')}
        {row.fields.length > 3 ? ` · ${row.fields.length - 3} more` : ''}
      </span>
    )
  }
  return null
}

/**
 * A row's undo, or why there is none (RFC 0313 3d). Redrawn from history after
 * every change, so an undo from the thread's card shows here too.
 */
export function RowAction({
  doc,
  row,
  undo,
}: {
  doc: unknown
  row: HistoryChange
  undo: (hash: string) => Promise<unknown>
}) {
  const [busy, setBusy] = useState(false)
  const status = useMemo(() => changeStatus(doc, row), [doc, row])
  const quiet = 'shrink-0 text-[11px] text-muted-foreground'
  if (status === 'undone') return <span className={quiet}>Undone</span>
  if (status === 'unsupported') return null
  if (status === 'changed') {
    return (
      <span
        className={`${quiet} line-through`}
        title="Changed since, so undo would revert later work"
      >
        Undo
      </span>
    )
  }
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => {
        setBusy(true)
        // A refusal needs no message of its own: the document changed under
        // the click, and the status redraws from history to say how.
        void undo(row.hash)
          .catch(() => {})
          .finally(() => setBusy(false))
      }}
      className="shrink-0 rounded-full border border-border px-2 text-[11px] text-foreground/80 hover:bg-muted disabled:opacity-50"
    >
      Undo
    </button>
  )
}
